/** Canonical ordered style set. */
export function danceStyles(dance) {
    const seen = new Set();
    return dance.styles.flatMap((value) => {
        const trimmed = value.trim();
        const normalized = trimmed.toLowerCase();
        if (!trimmed || seen.has(normalized))
            return [];
        seen.add(normalized);
        return [trimmed];
    });
}
/** Human-readable style label shared by lists, players, casting, and admin. */
export function danceStyleLabel(dance) {
    const styles = danceStyles(dance);
    return styles.length > 0 ? styles.join(" / ") : null;
}
/** "1:23:45" / "23:45" / "0:45" — program-style duration. */
export function formatDuration(totalSeconds) {
    const seconds = Math.max(0, Math.round(totalSeconds));
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
    const ss = String(s).padStart(2, "0");
    return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}
/** "1 hr 24 min" / "24 min" — friendly total for recital headers. */
export function formatDurationLong(totalSeconds) {
    const minutes = Math.max(1, Math.round(totalSeconds / 60));
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0)
        return `${m} min`;
    return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}
/** Dances a dancer appears in, in program order. */
export function dancesForDancer(dances, dancerId) {
    return dances.filter((dance) => dance.dancers.some((dancer) => dancer.dancerId === dancerId));
}
/** Dances any of the given dancers appear in (deduped), in program order. */
export function dancesForDancers(dances, dancerIds) {
    if (dancerIds.length === 0)
        return [];
    const wanted = new Set(dancerIds);
    return dances.filter((dance) => dance.dancers.some((dancer) => wanted.has(dancer.dancerId)));
}
/**
 * Case-insensitive dance search across title, program number, style, class,
 * and dancer names. "17" matches dance #17; "jazz" matches style; "emma"
 * matches dancers.
 */
export function searchDances(dances, query) {
    const q = query.trim().toLowerCase();
    if (!q)
        return dances;
    const asNumber = /^#?(\d+)$/.exec(q)?.[1];
    return dances.filter((dance) => {
        if (asNumber && dance.danceNumber === Number(asNumber))
            return true;
        if (dance.title.toLowerCase().includes(q))
            return true;
        if (danceStyles(dance).some((style) => style.toLowerCase().includes(q)))
            return true;
        if (dance.className?.toLowerCase().includes(q))
            return true;
        return dance.dancers.some((dancer) => dancer.name.toLowerCase().includes(q));
    });
}
/** Case-insensitive dancer-name search over a recital's dancer index. */
export function searchDancers(dancers, query) {
    const q = query.trim().toLowerCase();
    if (!q)
        return dancers;
    return dancers.filter((dancer) => dancer.name.toLowerCase().includes(q));
}
/** "emma-smith" from "Emma Smith". */
export function slugify(name) {
    return name
        .trim()
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}
